// Apps as an outside client sees them: its company's apps and where each one
// stands on the way to the store. The API sends a trimmed app (lib/client-app-view.ts
// on the worker); the full Apps page is for our staff.
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import api from '@/api/client';
import { Button } from '@/components/Button';
import { Modal } from '@/components/Modal';
import { useAuthStore } from '@/store/auth';

export type ClientPublishStatus = 'draft' | 'submitted' | 'in_review' | 'rejected' | 'published' | 'removed';

export interface ClientApp {
  id: string;
  name: string;
  platform: 'android' | 'ios';
  bundle_id: string;
  status: string;
  icon_url: string | null;
  description: string | null;
  short_description: string | null;
  store_url: string | null;
  store_version_android: string | null;
  released_at: string | null;
  created_at: string;
  publish_status: ClientPublishStatus;
  publish_note: string | null;
}

export const PUBLISH_STATUS: Record<ClientPublishStatus, { label: string; tone: string }> = {
  draft: { label: 'Draft', tone: 'bg-surface-muted text-content-muted' },
  submitted: { label: 'Submitted', tone: 'bg-primary/10 text-primary' },
  in_review: { label: 'In review', tone: 'bg-warning/15 text-warning' },
  rejected: { label: 'Needs changes', tone: 'bg-danger/10 text-danger' },
  published: { label: 'Live', tone: 'bg-success/15 text-success' },
  removed: { label: 'Removed from store', tone: 'bg-danger/10 text-danger' },
};

const BUNDLE_RE = /^[a-zA-Z][a-zA-Z0-9_]*(\.[a-zA-Z][a-zA-Z0-9_]*)+$/;

export function PublishStatusBadge({ status }: { status: ClientPublishStatus }) {
  const s = PUBLISH_STATUS[status] ?? PUBLISH_STATUS.draft;
  return <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${s.tone}`}>{s.label}</span>;
}

function storeLink(app: ClientApp): string {
  return app.store_url || `https://play.google.com/store/apps/details?id=${encodeURIComponent(app.bundle_id)}`;
}

export function ClientApps() {
  const qc = useQueryClient();
  const canEdit = useAuthStore((s) => s.canEdit());
  const appsQ = useQuery({ queryKey: ['apps'], queryFn: () => api.get<ClientApp[]>('/admin/apps') });
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<ClientApp | null>(null);
  const apps = appsQ.data ?? [];

  return (
    <div className="max-w-5xl mx-auto p-4 space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-content">Apps</h1>
          <p className="mt-1 text-sm text-content-muted">
            Your apps and their publishing status. Prepare the store listing and upload the build in Apps Setup.
          </p>
        </div>
        {canEdit && <Button onClick={() => setCreating(true)}>New app</Button>}
      </div>

      {appsQ.isLoading ? (
        <p className="text-sm text-content-muted">Loading…</p>
      ) : apps.length === 0 ? (
        <section className="rounded-lg border border-dashed border-border bg-surface/40 p-8 text-center">
          <p className="font-medium text-content">No apps yet</p>
          <p className="mt-1 text-sm text-content-subtle">
            Add your app with its Android package name, or build its listing from scratch in{' '}
            <Link to="/apps-setup" className="text-primary hover:underline">Apps Setup</Link>.
          </p>
        </section>
      ) : (
        <ul className="divide-y divide-border rounded-lg border border-border bg-surface">
          {apps.map((app) => (
            <li key={app.id} className="flex flex-wrap items-center gap-4 p-4">
              {app.icon_url ? (
                <img src={app.icon_url} alt="" className="h-12 w-12 flex-shrink-0 rounded-xl border border-border object-cover" />
              ) : (
                <div className="h-12 w-12 flex-shrink-0 rounded-xl border border-dashed border-border bg-surface-muted" />
              )}
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-medium text-content">{app.name}</span>
                  <PublishStatusBadge status={app.publish_status} />
                </div>
                <code className="text-xs text-content-subtle">{app.bundle_id}</code>
                {app.publish_status === 'rejected' && app.publish_note && (
                  <p className="mt-1 text-sm text-danger">{app.publish_note}</p>
                )}
                {app.publish_status === 'published' && app.store_version_android && (
                  <p className="mt-1 text-xs text-content-muted">Version {app.store_version_android} in Google Play</p>
                )}
              </div>
              <div className="flex flex-wrap gap-2">
                {(app.publish_status === 'published' || app.publish_status === 'removed') && (
                  <a href={storeLink(app)} target="_blank" rel="noreferrer"
                    className="inline-flex items-center rounded-md border border-border-strong px-3 py-1.5 text-sm text-content-muted hover:bg-surface-muted">
                    Google Play
                  </a>
                )}
                <Link to={`/apps-setup?tab=share&app=${encodeURIComponent(app.id)}`}
                  className="inline-flex items-center rounded-md border border-border-strong px-3 py-1.5 text-sm text-content-muted hover:bg-surface-muted">
                  Listing &amp; publishing
                </Link>
                {canEdit && <Button variant="secondary" size="sm" onClick={() => setEditing(app)}>Edit</Button>}
              </div>
            </li>
          ))}
        </ul>
      )}

      <AppForm
        key={editing?.id ?? (creating ? 'new' : 'none')}
        isOpen={creating || !!editing}
        app={editing}
        onClose={() => { setCreating(false); setEditing(null); }}
        onSaved={() => { setCreating(false); setEditing(null); void qc.invalidateQueries({ queryKey: ['apps'] }); }}
      />
    </div>
  );
}

function AppForm({ isOpen, app, onClose, onSaved }: {
  isOpen: boolean; app: ClientApp | null; onClose: () => void; onSaved: () => void;
}) {
  const [name, setName] = useState(app?.name ?? '');
  const [bundle, setBundle] = useState(app?.bundle_id ?? '');
  const bundleOk = BUNDLE_RE.test(bundle.trim());
  const save = useMutation({
    mutationFn: async () => {
      if (app) {
        return api.put(`/admin/apps/${app.id}`, {
          name: name.trim(),
          ...(bundle.trim() !== app.bundle_id ? { bundle_id: bundle.trim() } : {}),
        });
      }
      return api.post('/admin/apps', { name: name.trim(), platform: 'android', bundle_id: bundle.trim() });
    },
    onSuccess: onSaved,
  });

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={app ? 'Edit app' : 'New app'}>
      <form
        className="space-y-4"
        onSubmit={(e) => { e.preventDefault(); if (name.trim() && bundleOk) save.mutate(); }}
      >
        <div>
          <label className="block text-sm font-medium text-content-muted mb-1">Name</label>
          <input value={name} onChange={(e) => setName(e.target.value)} autoFocus
            className="w-full px-3 py-2 text-sm rounded-md border border-border bg-surface-muted text-content" />
        </div>
        <div>
          <label className="block text-sm font-medium text-content-muted mb-1">Android package name</label>
          <input value={bundle} onChange={(e) => setBundle(e.target.value)} placeholder="com.company.app"
            className="w-full px-3 py-2 text-sm font-mono rounded-md border border-border bg-surface-muted text-content" />
          <p className="mt-1 text-xs text-content-subtle">
            Must match the applicationId of your AAB. It cannot be changed after the first upload.
          </p>
        </div>
        {save.error && <p className="text-sm text-danger">{(save.error as Error).message}</p>}
        <div className="flex justify-end gap-2">
          <Button type="button" variant="secondary" onClick={onClose}>Cancel</Button>
          <Button type="submit" loading={save.isPending} disabled={!name.trim() || !bundleOk}>
            {app ? 'Save' : 'Create'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
